import useBugsnag from 'hooks/useBugsnag';

const client = require('./request/client');

const { notifyBugsnag } = useBugsnag();

const ENDPOINT = `${process.env.EP_URL}/client_api/id_verification.php`;

/**
 * POST to the ID verification endpoint and hand back the raw payload.
 *
 * A non-zero status is deliberately not turned into an Error the way the older
 * api modules do: callers must branch on the numeric `status` — notably `4`,
 * which means "already verified / pending review" and is not a failure.
 *
 * @returns {Promise<{status: number, message: string, output?: object}|{notFound: true}|null>}
 *          `{ notFound: true }` when the endpoint answered 404, null when the
 *          request never landed.
 */
const post = async (payload) => {
  const data = await client.sendRequest(ENDPOINT, payload);

  // A 404 means id_verification.php itself is not deployed on this environment,
  // as opposed to the request failing for any other reason. Callers may let the
  // user past the step in that case, so it is kept apart from the other failures.
  if (data instanceof Error && data.response?.status === 404) {
    notifyBugsnag({
      error: data,
      metaData: { scope: 'API REQUEST', xhr_data: { request: { url: ENDPOINT, request: payload.request } } },
    });
    return { notFound: true };
  }

  // sendRequest resolves with the axios error object when the request fails.
  if (!data || data instanceof Error || typeof data.status !== 'number') {
    const error = data instanceof Error ? data : new Error('Unable to reach the ID verification service');
    // The response body is deliberately left out of the metadata — a successful
    // one carries the token, which must never reach logs or analytics.
    notifyBugsnag({
      error,
      metaData: { scope: 'API REQUEST', xhr_data: { request: { url: ENDPOINT, request: payload.request } } },
    });
    return null;
  }

  return data;
};

/**
 * Flow A — mint a hand-off token for the signed-in user.
 *
 * No user identifier is sent: the backend derives the subject from the session
 * cookie, and someone else's id is rejected outright.
 */
const generateToken = () => post({ request: 'generate_id_verification_token' });

/**
 * Flow B — mint a hand-off token for a signup in progress.
 *
 * Needs no session; the subject is the temp user that `create_temp_user`
 * returned. An unknown or already-promoted srvrtid comes back as `status: 1`
 * with "You have specified an invalid user reference".
 */
const generateTempUserToken = (tempUserSrvrtid) =>
  post({ request: 'generate_temp_user_id_verification_token', temp_user_srvrtid: tempUserSrvrtid });

/**
 * Build the hand-off URL. Both flows redirect to the same place — only the way
 * the token was minted differs.
 *
 * @param {string} token      minted immediately before redirecting; it lives 30 minutes
 * @param {string} returnUrl  expresspaygh.com page the verification app returns the user to
 * @returns {string|null}     null when this environment has no verification app configured
 */
const buildHandoffUrl = (token, returnUrl) => {
  if (!process.env.ID_VERIFICATION_URL || !token) return null;

  return (
    `${process.env.ID_VERIFICATION_URL}?token=${encodeURIComponent(token)}` +
    `&return_url=${encodeURIComponent(returnUrl)}`
  );
};

/** What a generate-token response means for the caller. */
const HANDOFF = {
  REDIRECT: 'REDIRECT',
  SIGNED_OUT: 'SIGNED_OUT',
  NOTHING_TO_DO: 'NOTHING_TO_DO',
  /** The endpoint is not deployed here (404). */
  UNAVAILABLE: 'UNAVAILABLE',
  FAILED: 'FAILED',
};

/**
 * Turn a generate-token response into an outcome both flows can act on.
 *
 * Branching on the numeric `status` rather than on the message text is a
 * requirement of the integration contract, so it happens once here rather than
 * at each call site. What to *do* about an outcome differs between flows and
 * stays with the caller.
 *
 * @param {object|null} data       whatever generateToken/generateTempUserToken returned
 * @param {string}      returnUrl  page the verification app should come back to
 */
const interpretTokenResponse = (data, returnUrl) => {
  if (!data) return { outcome: HANDOFF.FAILED };
  if (data.notFound) return { outcome: HANDOFF.UNAVAILABLE };

  switch (data.status) {
    case 0: {
      const url = buildHandoffUrl(data.output?.token, returnUrl);
      return url ? { outcome: HANDOFF.REDIRECT, url } : { outcome: HANDOFF.FAILED };
    }
    case -1:
      return { outcome: HANDOFF.SIGNED_OUT, message: data.message };
    case 4:
      // Already verified, or documents are under review — not an error.
      return { outcome: HANDOFF.NOTHING_TO_DO, message: data.message };
    default:
      // status 1 and anything unrecognised. `message` is written for end users.
      return { outcome: HANDOFF.FAILED, message: data.message };
  }
};

export default {
  HANDOFF,
  generateToken,
  generateTempUserToken,
  buildHandoffUrl,
  interpretTokenResponse,
};
